<template>
  <u-deploy-panel :selected="!!selected" :sideinfo="true">
    <template slot="title">
      {{ $t('title') }}
    </template>
    <template slot="content">
      <div>
        <div class="text-subtitle-2">{{ $t('all_headline') }}</div>
        <p>{{ $t('all_content') }}</p>
      </div>
      <div class="mt-2">
        <div class="text-subtitle-2">{{ $t('collector_headline') }}</div>
        <p>{{ $t('collector_content') }}</p>
      </div>
      <v-container class="pa-0" fluid grid-list-xs text-center>
        <u-deploy-selection
          :title="$t('deployments')"
          :items="deployments"
          :selected="selected"
          @toggle="toggle"
        ></u-deploy-selection>
      </v-container>
    </template>
    <template slot="actions">
      <a :href="doc" target="_blank">{{ $t('wizard.more') }}</a>
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('cancel')">{{ $t('wizard.cancel') }}</v-btn>
      <v-btn text @click="$emit('prev')">{{ $t('wizard.prev') }}</v-btn>
      <v-btn color="primary" :disabled="!selected" @click="$emit('next', selected)">{{ $t('wizard.next') }}</v-btn>
    </template>
    <template slot="empty">
      {{ $t('select') }}
    </template>
    <template slot="selection" v-if="selected">
      {{ $t('items.' + selected.id + '.title') }}
    </template>
    <template slot="info" v-if="selected">
      <v-tab ripple>{{ $t('tabs.info') }}</v-tab>
      <v-tab-item>
        <u-deploy-info
          :title="$t('info.description')"
          :info="$t('items.' + selected.id + '.description')"
          :screenshot="selected.screenshot"
        ></u-deploy-info>
        <u-deploy-info
          :title="$t('info.features')"
          :info="$t('items.' + selected.id + '.features')"
        ></u-deploy-info>
        <u-deploy-info
          :title="$t('info.requirements')"
          :info="$t('items.' + selected.id + '.requirements')"
        ></u-deploy-info>
        <div v-if="selected.faq" class="mt-3">
          <i18n path="info.faq">
            <a place="faq" :href="selected.doc" target="_blank">{{ $t('installation') }}</a>
          </i18n>
        </div>
      </v-tab-item>
      <v-tab ripple disabled>{{ $t('tabs.deployed') }}</v-tab>
      <v-tab-item class="mt-3 ml-3">
        <div v-if="selected">
          <div>{{ $t('deployed.total', { total: onPremiseTotal, device: selected.type }) }}</div>
          <ul>
            <li>{{ $t('deployed.onprem', { total: onPremiseTotal }) }}</li>
            <li>{{ $t('deployed.aws', { total: 0 }) }}</li>
            <li>{{ $t('deployed.azure', { total: 0 }) }}</li>
            <li>{{ $t('deployed.google', { total: 0 }) }}</li>
          </ul>
        </div>
      </v-tab-item>
    </template>
  </u-deploy-panel>
</template>

<script>
import DeployInfoComponent from '@/components/deploy/Info.vue'
import DeployPanelComponent from '@/components/deploy/Panel.vue'
import DeploySelectionComponent from '@/components/deploy/Selection.vue'
import NetworkService from '@/services/network'
import { docsUrl } from '@/services/docs'

export default {
  data () {
    return {
      selected: null,
      deployments: [
        {
          id: 'all',
          available: true,
          name: this.$t('items.all.name'),
          type: this.$t('items.all.type'),
          image: `${require('../assets/all.png')}`,
          faq: true,
          doc: docsUrl('quickstart'),
          password: true,
          warnIfNotEmpty: true
        },
        {
          id: 'collector',
          available: true,
          name: this.$t('items.collector.name'),
          type: this.$t('items.collector.type'),
          image: `${require('../assets/collector.svg')}`,
          faq: true,
          doc: docsUrl('cloudstart', 'deploy-your-first-collector'),
          password: false
        }
      ],
      onPremiseTotal: 0,
      doc: docsUrl('manage')
    }
  },
  methods: {
    toggle (item) {
      this.selected = this.selected === item ? null : item
      if (this.selected) {
        this.getDeployedCount()
      }
    },
    getDeployedCount () {
      const count = NetworkService.nodes().then((data) => {
        const groups = data.map((node) => {
          return node.group || 'unknown'
        })
        const selectedGroup = this.getSelectedGroup(this.selected.id)
        this.onPremiseTotal = groups.reduce((acc, current) => {
          return current === selectedGroup ? acc + 1 : acc
        }, 0)
      })
    },
    getSelectedGroup (id) {
      switch (id) {
        case 'collector':
          return 'collector'
        case 'metricdb':
        case 'eventdb':
          return 'database'
        case 'portal':
          return 'portal'
        case 'report':
          return 'report'
        default:
          break
      }
    }
  },
  components: {
    'u-deploy-info': DeployInfoComponent,
    'u-deploy-panel': DeployPanelComponent,
    'u-deploy-selection': DeploySelectionComponent
  }
}
</script>

<style lang="scss" scoped>
.description {
  white-space: pre-wrap;
}
</style>

<i18n>
{
  "en": {
    "title": "Select a deployment option",
    "all_headline": "All-In-One Deployment",
    "all_content": "Installs the complete Unryo Platform on your host. Unryo is a full-stack observability platform with AI automation that connects your infrastructure and monitoring tools to discover dependencies, correlate events, and highlight issues. Operations teams get clear insights, dynamic maps, root-cause analysis, and interact with AI agents to resolve issues faster.",
    "collector_headline": "Collector-Only Deployment",
    "collector_content": "Installs only the Unryo Collector on your host. Cloud customers only need collectors, since the full platform is already hosted and managed in Unryo Cloud.",
    "deployments": "Deployment Methods",
    "installation": "installation",
    "select": "Please select an unryo component",
    "tabs": {
      "default": {
        "title": "Please select an unryo component"
      },
      "info": "Info",
      "deployed": "Deployed"
    },
    "info": {
      "description": "Description",
      "features": "Features",
      "requirements": "Requirements",
      "faq": "See {faq} documentation."
    },
    "deployed": {
      "total": "You have {total} {device} currently deployed: ",
      "onprem": "{total} on-premises",
      "aws": "{total} in AWS",
      "azure": "{total} in Azure Web Services",
      "google": "{total} in Google Cloud Services"
    },
    "items": {
      "all": {
        "name": "All in one deployment",
        "type": "All In one",
        "title": "All in one deployment",
        "description": "Unryo is a performance and analytic solution that monitors all your infrastructure, datacenters, clouds, and services, across the full stack, and at scale. \n\nThe platform collects metrics, events, logs, and meta-data from all sources, detects anomalies and performance problems in real-time, and delivers valuable insights and dashboards for your users and customers.",
        "features": [
          "Extensible (add predefined collection templates).",
          "Full Stack Observability: metrics, logs, APM",
          "Hundreds of out-of-the-box integrations",
          "Streaming Analytic and Alerting",
          "Centralized UI for configurations & tagging, with secure encrypted vault",
          "One-click Admin Console (easy deployment, automatic upgrades, topology maps)",
          "REST API Server"
        ],
        "requirements": {
          "system": {
            "prefix": "See",
            "text": "System Requirements",
            "url": "quickstart",
            "id": "operating-system-compatibility"
          },
          "network": {
            "prefix": "See", 
            "text": "Network Requirements",
            "url": "quickstart",
            "id": "network-requirements"
          }
        }
      },
      "collector": {
        "name": "Deploy a single collector",
        "type": "Collector(s)",
        "title": "The Unryo Collector",
        "description": "The Unryo Collector collects metric data and event logs from your infrastructure.\n\nIt is a lightweight, non intrusive and extensible agent. Once deployed, you will be able to add one or multiple Machine Learning Packs, which are turnkey integrations that collect a predefined set of metrics and logs from popular devices, applications and domains. The Unryo Collector also gives you the option to configure your own data collection.",
        "features": [
          "Collect time-serie metrics.",
          "Collect log events.",
          "Extensible (add predefined collection templates)."
        ],
        "requirements": {
          "system": {
            "prefix": "See",
            "text": "Sizing Recommendations",
            "url": "quickstart",
            "id": "server-sizing"
          },
          "network": {
            "prefix": "See", 
            "text": "Network Prerequisites",
            "url": "quickstart",
            "id": "network-requirements"
          }
        }
      }
    }
  }
}
</i18n>
